import jsonLogic from 'json-logic-js';

/**
 * Shared conditional-logic evaluator — WEB PORT (ULLR-150).
 *
 * SOURCE OF TRUTH: `UllrServerless/shared/models/form/conditionEvaluator.ts`.
 * This file is a faithful port — same compile→evaluate structure, masking
 * semantics, fail-open rules, and answered semantics. Any change starts on the
 * backend, updates the shared vector file there, and is re-ported here. The
 * cross-client contract gate is `conditionEvaluatorTestVectors.json` (copied
 * verbatim from the backend): every vector must pass.
 *
 * A `simple` ConditionConfiguration is COMPILED into a JSONLogic rule (one
 * clause per condition, wrapped in the configuration's `and`/`or`) and
 * evaluated with `json-logic-js` against `{ answers: { [questionId]: value } }`.
 * The compile step is deliberately the only place operator semantics live, so
 * a future `{type: 'jsonlogic', rule}` envelope variant can skip compilation
 * and feed the engine directly.
 *
 * Semantics:
 * - A question with no `displayCondition` is always visible. Unknown envelope
 *   types fail OPEN (visible / static `required`) so degradation matches
 *   old-client behavior.
 * - While hidden, a question is never required. A present `requiredCondition`
 *   overrides the static `required` flag of a visible question.
 * - Chains: a hidden controller's (possibly stale) answer is masked, so its
 *   dependents' conditions evaluate as if it were unanswered.
 * - "Answered" means value !== undefined/null/'' and, for arrays, length > 0.
 *   A number 0 and boolean false ARE answers.
 */

// Wire types, mirroring the backend's shared/types.ts (the web has no shared
// types module; the builder's logicModel declares the same shapes for editing).
export type ConditionOperator =
  | 'equals' | 'notEquals'
  | 'contains'
  | 'lessThan' | 'greaterThan'
  | 'lessThanOrEqual' | 'greaterThanOrEqual'
  | 'answered' | 'notAnswered';

export interface QuestionCondition {
  dependsOnQuestionId: string;
  operator: ConditionOperator;
  value?: string | number;
}

export interface SimpleConditionConfiguration {
  type: 'simple';
  operator: 'and' | 'or';
  conditions: QuestionCondition[];
}

export type ConditionConfiguration = SimpleConditionConfiguration | { type: string, [key: string]: any };

type JsonLogicRule = Record<string, any>;

// Minimal structural shapes so the evaluator stays pure and portable: the web
// Form/QuestionSet/Question model classes satisfy these (answers live on
// `question.value`), as do the shared test vectors.
export interface EvaluatableQuestion {
  id: string;
  required?: boolean;
  value?: any;
  displayCondition?: ConditionConfiguration;
  requiredCondition?: ConditionConfiguration;
}
export interface EvaluatableForm {
  questionSets: Array<{ questions: EvaluatableQuestion[] }>;
}

export const isAnswered = (value: any): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
};

// Pure-JSONLogic "answered" check matching isAnswered():
// - `missing` treats undefined/null/'' as missing (0 and false are NOT missing)
// - the `.length` clause rejects empty arrays; non-array values have no
//   `length` var (-> null), and null != 0, so they pass through
const answeredRule = (answerPath: string): JsonLogicRule => ({
  and: [
    { '!': { missing: [answerPath] } },
    { '!=': [{ var: `${answerPath}.length` }, 0] },
  ],
});

const COMPARISON_JSONLOGIC_OPERATORS: Record<string, string> = {
  lessThan: '<',
  greaterThan: '>',
  lessThanOrEqual: '<=',
  greaterThanOrEqual: '>=',
};

// `contains` semantics by answer shape: array answers (multi-select) use exact
// membership of the option's stable value; string answers (short/long text)
// use CASE-INSENSITIVE substring matching. Registered as a custom JSONLogic
// operation so the compiled rule stays declarative; other answer shapes never
// match. (Registration is module-scoped and idempotent.)
jsonLogic.add_operation('ullrContains', (value: any, answer: any): boolean => {
  if (Array.isArray(answer)) {
    return answer.indexOf(value) !== -1;
  }
  if (typeof answer === 'string') {
    return answer.toLowerCase().indexOf(String(value).toLowerCase()) !== -1;
  }
  return false;
});

// Returns undefined when the condition's operator is unknown — callers treat
// the whole configuration as unevaluable and fail open.
const compileCondition = (condition: QuestionCondition): JsonLogicRule | undefined => {
  const answerPath = `answers.${condition.dependsOnQuestionId}`;
  const answerVar = { var: answerPath };
  // Every value-taking operator is guarded with answeredness so a condition on
  // an unanswered question is false (e.g. `notEquals` must not fire before the
  // source question has been answered at all).
  switch (condition.operator) {
    case 'answered':
      return answeredRule(answerPath);
    case 'notAnswered':
      return { '!': answeredRule(answerPath) };
    case 'equals':
      return { and: [answeredRule(answerPath), { '==': [answerVar, condition.value] }] };
    case 'notEquals':
      return { and: [answeredRule(answerPath), { '!=': [answerVar, condition.value] }] };
    case 'contains':
      return { and: [answeredRule(answerPath), { ullrContains: [condition.value, answerVar] }] };
    case 'lessThan':
    case 'greaterThan':
    case 'lessThanOrEqual':
    case 'greaterThanOrEqual':
      return { and: [answeredRule(answerPath), { [COMPARISON_JSONLOGIC_OPERATORS[condition.operator] as string]: [answerVar, condition.value] }] };
    default:
      return undefined;
  }
};

// Compiles a `simple` configuration to a single JSONLogic rule. Returns
// undefined for unknown envelope types or malformed configurations — callers
// fail OPEN (visible / static required).
export const compileConditionConfiguration = (configuration: ConditionConfiguration): JsonLogicRule | undefined => {
  if (!configuration || (configuration as any).type !== 'simple') {
    return undefined;
  }
  const simple = configuration as SimpleConditionConfiguration;
  if (simple.operator !== 'and' && simple.operator !== 'or') {
    return undefined;
  }
  if (!Array.isArray(simple.conditions) || !simple.conditions.length) {
    return undefined;
  }
  const clauses: JsonLogicRule[] = [];
  for (const condition of simple.conditions) {
    const clause = condition && compileCondition(condition);
    if (!clause) {
      return undefined;
    }
    clauses.push(clause);
  }
  return { [simple.operator]: clauses };
};

type EvaluationContext = {
  questionsById: Record<string, EvaluatableQuestion>;
  visibilityById: Record<string, boolean>;
};

const buildEvaluationContext = (completedForm: EvaluatableForm): EvaluationContext => {
  const questionsById: Record<string, EvaluatableQuestion> = {};
  for (const questionSet of (completedForm?.questionSets || [])) {
    for (const question of (questionSet?.questions || [])) {
      questionsById[question.id] = question;
    }
  }
  return { questionsById, visibilityById: {} };
};

// Evaluates one configuration against the form's answers, masking the answers
// of hidden controllers. Returns undefined when the configuration is
// unevaluable (unknown envelope type / operator) so callers can fail open.
const evaluateConfiguration = (
  configuration: ConditionConfiguration,
  context: EvaluationContext,
  visiting: Set<string>,
): boolean | undefined => {
  const rule = compileConditionConfiguration(configuration);
  if (!rule) {
    return undefined;
  }
  const answers: Record<string, any> = {};
  for (const condition of (configuration as SimpleConditionConfiguration).conditions) {
    const sourceId = condition.dependsOnQuestionId;
    const sourceQuestion = context.questionsById[sourceId];
    if (sourceQuestion && _isVisibleById(sourceId, context, visiting)) {
      answers[sourceId] = sourceQuestion.value;
    }
  }
  return !!jsonLogic.apply(rule as any, { answers });
};

const _isVisibleById = (questionId: string, context: EvaluationContext, visiting: Set<string>): boolean => {
  const memoized = context.visibilityById[questionId];
  if (memoized !== undefined) {
    return memoized;
  }
  const question = context.questionsById[questionId];
  if (!question) {
    return true;
  }
  const configuration = question.displayCondition;
  if (configuration === undefined || configuration === null) {
    context.visibilityById[questionId] = true;
    return true;
  }
  // Template validation's ordering rule makes cycles impossible in valid
  // forms; guard anyway (stored data is not re-validated here) and fail open.
  if (visiting.has(questionId)) {
    return true;
  }
  visiting.add(questionId);
  const result = evaluateConfiguration(configuration, context, visiting);
  visiting.delete(questionId);
  const isVisible = result === undefined ? true : result;
  context.visibilityById[questionId] = isVisible;
  return isVisible;
};

export const isQuestionVisible = (question: EvaluatableQuestion, completedForm: EvaluatableForm): boolean => {
  const context = buildEvaluationContext(completedForm);
  // tolerate a question object that isn't (or isn't yet) part of the form
  if (!context.questionsById[question.id]) {
    context.questionsById[question.id] = question;
  }
  return _isVisibleById(question.id, context, new Set());
};

export const isQuestionRequired = (question: EvaluatableQuestion, completedForm: EvaluatableForm): boolean => {
  const context = buildEvaluationContext(completedForm);
  if (!context.questionsById[question.id]) {
    context.questionsById[question.id] = question;
  }
  // hidden questions are never required, regardless of any flags
  if (!_isVisibleById(question.id, context, new Set())) {
    return false;
  }
  const configuration = question.requiredCondition;
  if (configuration === undefined || configuration === null) {
    return !!question.required;
  }
  const result = evaluateConfiguration(configuration, context, new Set([question.id]));
  // unknown envelope type: fall back to the static flag (fail open)
  if (result === undefined) {
    return !!question.required;
  }
  return result;
};
